import { useEffect, useState } from 'react';
import { subscribe } from '../../hooks/useWebSocket';
import type { EventListItem } from '../../api/types';
import {
  triggerManualCircuitBreaker,
  type ManualCircuitBreakerAction,
} from '../../api/systemApi';
import { ManualCircuitBreakerModal } from './ManualCircuitBreakerModal';
import { isAlertSent } from './liveMonitor.types';

// 권위: docs/ARCH_DASHBOARD.md §4.5 우측 패널.
// - 수동 Circuit Breaker 3버튼 (위험 수준: WARN/DANGER/CRITICAL)
// - 확인 모달 → 서버 호출 → 토스트 → 이벤트 스트림에 FATAL 자동 반영 (서버 주도)
// - Alert 이력 (최근 10건) — `/topic/system-events` 의 `OBS.ALERT.SENT` 이벤트만 필터링

interface ToastState {
  tone: 'ok' | 'error';
  message: string;
}

const ALERT_MAX = 10;

export function LiveMonitorActionPanel(): JSX.Element {
  const [modalAction, setModalAction] = useState<ManualCircuitBreakerAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [alerts, setAlerts] = useState<EventListItem[]>([]);

  useEffect(() => {
    const off = subscribe('/topic/system-events', (msg) => {
      if (!msg.body) return;
      try {
        const parsed = JSON.parse(msg.body) as Record<string, unknown>;
        const ev = normalizeAlert(parsed);
        if (!ev || !isAlertSent(ev)) return;
        setAlerts((prev) => {
          if (prev.some((p) => p.eventId === ev.eventId)) return prev;
          const next = [ev, ...prev];
          if (next.length > ALERT_MAX) next.length = ALERT_MAX;
          return next;
        });
      } catch {
        /* ignore */
      }
    });
    return off;
  }, []);

  // 토스트 자동 소멸 3.5초.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(id);
  }, [toast]);

  async function handleConfirm(
    action: ManualCircuitBreakerAction,
    reason: string
  ): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const resp = await triggerManualCircuitBreaker(action, reason);
      setToast({
        tone: 'ok',
        message: `발동: ${action} (event ${shortId(resp.eventId)})`,
      });
      setModalAction(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setToast({ tone: 'error', message: `발동 실패: ${msg}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="live-panel" aria-label="수동 제어 및 알림">
      <header className="live-panel__header">
        <h2 className="live-panel__title">수동 제어 · 알림</h2>
      </header>

      <div className="live-panel__body">
        <div className="live-cb-buttons">
          <div className="live-cb-buttons__label">수동 Circuit Breaker</div>
          <button
            type="button"
            className="live-cb-btn live-cb-btn--warn"
            onClick={() => setModalAction('HALT_NEW_ORDERS')}
          >
            <span className="live-cb-btn__name">HALT_NEW_ORDERS</span>
            <span className="live-cb-btn__hint">신규 주문 차단</span>
          </button>
          <button
            type="button"
            className="live-cb-btn live-cb-btn--danger"
            onClick={() => setModalAction('REDUCE_ALL_50PCT')}
          >
            <span className="live-cb-btn__name">REDUCE_ALL_50PCT</span>
            <span className="live-cb-btn__hint">전 포지션 50% 축소</span>
          </button>
          <button
            type="button"
            className="live-cb-btn live-cb-btn--critical"
            onClick={() => setModalAction('HALT_ALL')}
          >
            <span className="live-cb-btn__name">HALT_ALL</span>
            <span className="live-cb-btn__hint">전체 매매 중단</span>
          </button>
        </div>

        <div className="live-alerts">
          <div className="live-alerts__label">Alert 이력 (최근 {ALERT_MAX}건)</div>
          {alerts.length === 0 ? (
            <div className="syslog-empty">Teams/Email 발송 이벤트 대기 중…</div>
          ) : (
            <ul className="live-alerts__list">
              {alerts.map((a) => (
                <li key={a.eventId} className="live-alerts__item">
                  <span className="live-alerts__time">{formatTime(a.occurredAt)}</span>
                  <code className="live-alerts__code">{a.eventCode}</code>
                  <span className="live-alerts__summary">{a.summary || '(no summary)'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {modalAction && (
        <ManualCircuitBreakerModal
          action={modalAction}
          onClose={() => {
            if (!busy) {
              setModalAction(null);
              setError(null);
            }
          }}
          onConfirm={handleConfirm}
          busy={busy}
          error={error}
        />
      )}

      {toast && (
        <div
          className={`live-toast live-toast--${toast.tone}`}
          role={toast.tone === 'error' ? 'alert' : 'status'}
        >
          {toast.message}
        </div>
      )}
    </section>
  );
}

function normalizeAlert(p: Record<string, unknown>): EventListItem | null {
  const eventId =
    (p.eventId as string | undefined) ??
    (p.event_id as string | undefined) ??
    (p.id as string | undefined);
  const severity = ((p.severity as string | undefined) ?? '').toUpperCase();
  const eventCode =
    (p.eventCode as string | undefined) ?? (p.event_code as string | undefined) ?? '';
  if (!eventId || !severity || !eventCode) return null;
  if (!['FATAL', 'ERROR', 'WARN', 'INFO', 'DEBUG'].includes(severity)) return null;
  return {
    eventId,
    occurredAt:
      (p.occurredAt as string | undefined) ??
      (p.occurred_at as string | undefined) ??
      new Date().toISOString(),
    severity: severity as EventListItem['severity'],
    sourceAgent:
      (p.sourceAgent as string | undefined) ?? (p.source_agent as string | undefined) ?? '',
    eventCode,
    summary: (p.summary as string | undefined) ?? '',
    entityRefs:
      (p.entityRefs as Record<string, unknown> | undefined) ??
      (p.entity_refs as Record<string, unknown> | undefined) ??
      null,
    traceId:
      (p.traceId as string | undefined) ?? (p.trace_id as string | undefined) ?? null,
    acked: false,
  };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}
